import React from 'react';
import {
  Button, Card, MessageBar, MessageBarBody, Text, Title1, makeStyles, tokens,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular, BugRegular, HomeRegular } from '@fluentui/react-icons';
import { useErrorReporting } from '../contexts/ErrorReportingContext';

interface State { error?: Error; componentStack?: string }

interface BoundaryProps extends React.PropsWithChildren {
  onError: (error: Error, componentStack?: string) => void;
  onReport: (error: Error, componentStack?: string) => void;
}

const useStyles = makeStyles({
  page: {
    minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '32px',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: { width: 'min(640px, 100%)', padding: '32px', gap: '20px' },
  heading: { display: 'flex', flexDirection: 'column', gap: '8px' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  reference: { overflowWrap: 'anywhere', color: tokens.colorNeutralForeground3 },
});

const RecoveryPage: React.FC<{ error: Error; onReport: () => void }> = ({ error, onReport }) => {
  const styles = useStyles();
  return (
    <main className={styles.page}>
      <Card className={styles.card}>
        <div className={styles.heading}>
          <Title1>Something went wrong</Title1>
          <Text size={400}>The unexpected error was contained. Try reloading the application or return home.</Text>
        </div>
        <MessageBar intent="error"><MessageBarBody>The current page cannot continue safely.</MessageBarBody></MessageBar>
        <Text className={styles.reference} size={200}>Error type: {error.name || 'Error'}</Text>
        <div className={styles.actions}>
          <Button appearance="primary" icon={<BugRegular />} onClick={onReport}>Report this problem</Button>
          <Button icon={<ArrowClockwiseRegular />} onClick={() => window.location.reload()}>Reload</Button>
          <Button icon={<HomeRegular />} onClick={() => window.location.assign('/overview')}>Return home</Button>
        </div>
      </Card>
    </main>
  );
};

class Boundary extends React.Component<BoundaryProps, State> {
  state: State = {};
  static getDerivedStateFromError(error: Error): State { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const componentStack = info.componentStack || undefined;
    this.setState({ componentStack });
    this.props.onError(error, componentStack);
  }
  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <RecoveryPage
        error={this.state.error}
        onReport={() => this.props.onReport(this.state.error!, this.state.componentStack)}
      />
    );
  }
}

const AppErrorBoundary: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { rememberError, reportError } = useErrorReporting();
  return (
    <Boundary
      onError={rememberError}
      onReport={(error, componentStack) => reportError({ error, componentStack })}
    >
      {children}
    </Boundary>
  );
};

export default AppErrorBoundary;